/**
 * Meta-progressão da Vila — prédios Sutarifu (M3, AC-M3-05). 5 trilhas × 10 níveis, cada uma com um
 * EFEITO MECÂNICO na sim. Todas as curvas são NO-OP no nível 0 (multiplicador ×1 / bônus 0), para que
 * uma run sem meta seja byte-idêntica à sim base (protege o golden de determinismo).
 *   Curandeiro → HP inicial · Corredor → velocidade · Ferreiro → dano das armas ·
 *   Mágico → carga do especial · Sortudo → rerolls do draft + curva de raridade.
 */
import type { Rng } from './rng.ts';

export type Raridade = 'comum' | 'raro' | 'epico' | 'lendario';

export interface SutarifuLevels {
  curandeiro: number;
  ferreiro: number;
  corredor: number;
  sortudo: number;
  magico: number;
}

export const NENHUM_BONUS: SutarifuLevels = { curandeiro: 0, ferreiro: 0, corredor: 0, sortudo: 0, magico: 0 };
export const SUTARIFU_NIVEL_MAX = 10;

const nivel = (n: number): number => (n < 0 ? 0 : n > SUTARIFU_NIVEL_MAX ? SUTARIFU_NIVEL_MAX : Math.floor(n));

export function bonusHpInicial(n: number): number { return 1 + 0.05 * nivel(n); } // Curandeiro: +5%/nv
export function bonusVelocidade(n: number): number { return 1 + 0.02 * nivel(n); } // Corredor: +2%/nv
export function bonusDanoArmas(n: number): number { return 1 + 0.05 * nivel(n); } // Ferreiro: +5%/nv
export function bonusCargaEspecial(n: number): number { return 1 + 0.06 * nivel(n); } // Mágico: +6%/nv

// ── Sortudo ──────────────────────────────────────────────────────────────────
/** rerolls extra por nível do Sortudo: +1/+2/+3 (A.6), saturando em 3. */
export function rerollBonus(n: number): number { return Math.min(3, nivel(n)); }
/** rerolls disponíveis na run: 1 grátis + bônus do Sortudo (teto 4, A.6). */
export function rerollsDisponiveis(sortudo: number): number { return 1 + rerollBonus(sortudo); }

const RARIDADE_BASE = [50, 28, 16, 6] as const; // comum/raro/épico/lendário (doc 03 §5.4)

/** pesos de raridade deslocados pela sorte: tira do comum e move p/ raro/épico/lendário. */
export function rarezaPesos(sortudo: number): [number, number, number, number] {
  const shift = Math.min(40, 4 * nivel(sortudo)); // pontos retirados do comum
  return [
    RARIDADE_BASE[0] - shift,
    RARIDADE_BASE[1] + shift * 0.5,
    RARIDADE_BASE[2] + shift * 0.3,
    RARIDADE_BASE[3] + shift * 0.2,
  ];
}

const TIERS: readonly Raridade[] = ['comum', 'raro', 'epico', 'lendario'];

/** sorteia uma raridade segundo a curva do Sortudo (determinístico via rng). */
export function sorteiaRaridade(rng: Rng, sortudo: number): Raridade {
  const p = rarezaPesos(sortudo);
  const total = p[0] + p[1] + p[2] + p[3];
  let roll = rng.next01() * total;
  for (let i = 0; i < 4; i++) {
    roll -= p[i]!;
    if (roll <= 0) return TIERS[i]!;
  }
  return 'lendario';
}

/** níveis extras que uma carta ganha pela raridade (épico +1, lendário +2). */
export function bonusNivelPorRaridade(r: Raridade | undefined): number {
  return r === 'lendario' ? 2 : r === 'epico' ? 1 : 0;
}
